<template>
  <div>
    <ValidationObserver ref="form" v-slot="{ invalid }">
      <v-overlay :value="loading" absolute>
        <v-progress-circular indeterminate size="64"></v-progress-circular>
      </v-overlay>
      <div v-if="docs">
        <!-- Basic Information -->
        <v-subheader class="px-0">基本資料</v-subheader>
        <v-row dense>
          <v-col cols="12" md="6">
            <ValidationProvider v-slot="{ errors }" name="代理名稱" rules="required">
              <v-text-field
                v-model="docs.name"
                :error="!!errors.length"
                :error-messages="errors"
                flat
                label="代理名稱 *"
                outlined
                dense
                hide-details="auto"
              />
            </ValidationProvider>
          </v-col>
          <v-col cols="12" md="6">
            <ValidationProvider v-slot="{ errors }" name="電話">
              <v-text-field
                v-model="docs.phone"
                :error="!!errors.length"
                :error-messages="errors"
                flat
                label="電話"
                outlined
                dense
                hide-details="auto"
              />
            </ValidationProvider>
          </v-col>
        </v-row>

        <!-- Action Buttons -->
        <v-row dense class="mt-4">
          <v-col class="d-flex align-center gap-2">
            <v-btn :disabled="invalid || saving" :loading="saving" color="primary" @click="save">
              立即提交
            </v-btn>
            <v-spacer />

            <v-btn :disabled="saving" color="default" outlined @click="resetForm"> 重新填寫 </v-btn>

            <v-btn
              v-if="!isNewRecord"
              :disabled="saving"
              color="error"
              outlined
              @click="confirmDelete"
            >
              刪除
            </v-btn>
          </v-col>
        </v-row>
      </div>
    </ValidationObserver>
  </div>
</template>

<script>
import api from '@/api';
import { crudRead, crudUpdate } from '@/api/crud';
import { RESTFUL } from '@/data/constants';
import ApiError from '@/api/ApiError';

export default {
  name: 'AgentDetailsForm',
  props: {
    id: {
      type: String,
      default: 'new',
    },
  },
  data() {
    return {
      docs: null,
      loading: false,
      saving: false,
    };
  },
  mounted() {
    this.reload();
  },
  computed: {
    isNewRecord() {
      return !this.id || this.id === 'new';
    },
  },
  methods: {
    async reload() {
      if (this.isNewRecord) {
        // Initialize new record with default values
        this.docs = {
          name: '',
          phone: '',
          type: 'agent',
        };
      } else {
        this.loading = true;
        try {
          this.docs = await crudRead(api, RESTFUL.agent.read, this.id);
        } catch (e) {
          const ae = ApiError.wrap(e);
          await this.$store.dispatch('alert/updateAlertMessage', {
            msg: ae.message,
            type: 'error',
            color: 'error',
          });
        } finally {
          this.loading = false;
        }
      }
    },
    resetForm() {
      // Reset form validation
      this.$refs.form?.reset();
      // Reload data
      this.reload();
    },
    async confirmDelete() {
      const confirmed = await this.$root.$confirm.open(
        '確認刪除',
        '確定要刪除此記錄嗎？此操作無法復原。',
        {
          color: 'red',
        },
        '確定刪除',
        '取消',
      );
      if (!confirmed) {
        return;
      }
      this.deleteRecord();
    },
    async deleteRecord() {
      try {
        await api.delete(`${RESTFUL.agent.read}/${this.id}`);

        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: '刪除成功',
          type: 'success',
          color: 'success',
        });

        // Emit deleted event
        this.$emit('deleted');
      } catch (e) {
        const ae = ApiError.wrap(e);
        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: ae.message,
          type: 'error',
          color: 'error',
        });
      }
    },
    async save() {
      this.saving = true;
      try {
        if (!(await this.$refs.form?.validate?.())) {
          this.saving = false;
          return;
        }

        // Prepare payload
        const payload = {
          name: this.docs.name,
          phone: this.docs.phone,
          type: 'agent',
        };

        if (this.isNewRecord) {
          await api.post(RESTFUL.agent.create, payload);
        } else {
          await crudUpdate(api, RESTFUL.agent.read, this.id, payload);
        }

        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: '儲存成功',
          type: 'success',
          color: 'success',
        });

        // Emit saved event
        this.$emit('saved');
      } catch (e) {
        const ae = ApiError.wrap(e);
        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: ae.message,
          type: 'error',
          color: 'error',
        });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
